import React, { useState } from 'react';
import { apiCall } from './config';

function QuickWhatsAppImporter({ userId, userName, onImportComplete }) {
  const [bulkText, setBulkText] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [importStatus, setImportStatus] = useState('');

  const handleBulkImport = async () => {
    if (!bulkText.trim()) return;

    setIsProcessing(true);
    setImportStatus('Verarbeite Text...');

    try {
      // Parse den Text nach verschiedenen Formaten
      const messages = parseMultipleFormats(bulkText);
      
      if (messages.length === 0) {
        setImportStatus('❌ Keine gültigen Nachrichten gefunden');
        setIsProcessing(false);
        return;
      }

      setImportStatus(`${messages.length} Nachrichten gefunden. Importiere...`);
      
      // Sende an Backend
      const response = await apiCall(`/users/${userId}/whatsapp/import`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ messages })
      });

      if (response.ok) {
        const result = response;
        setImportStatus(`✅ ${result.imported} Nachrichten erfolgreich importiert!`);
        setBulkText(''); // Clear textarea
        
        if (onImportComplete) {
          setTimeout(() => onImportComplete(), 1000);
        }
      } else {
        setImportStatus('❌ Import fehlgeschlagen');
      }
    } catch (error) {
      console.error('Import-Fehler:', error);
      setImportStatus('❌ Fehler beim Verarbeiten');
    }

    setIsProcessing(false);
  };

  const parseMultipleFormats = (text) => {
    const messages = [];
    const lines = text.split('\n').filter(line => line.trim());
    
    for (const line of lines) {
      let parsed = null;
      
      // Format 1: WhatsApp Export [DD.MM.YY, HH:MM:SS] Name: Message
      parsed = parseWhatsAppExport(line);
      if (parsed) {
        messages.push(parsed);
        continue;
      }
      
      // Format 2: WhatsApp Web Copy/Paste "HH:MM Name Message"
      parsed = parseWhatsAppWeb(line);
      if (parsed) {
        messages.push(parsed);
        continue;
      }
      
      // Format 3: Simple "Name: Message" 
      parsed = parseSimpleFormat(line);
      if (parsed) {
        messages.push(parsed);
        continue;
      }
      
      // Format 4: Nur Text (wird als incoming behandelt)
      if (line.trim().length > 0) {
        messages.push({
          message: line.trim(),
          message_type: 'incoming',
          timestamp: new Date().toISOString().slice(0, 19).replace('T', ' '),
          status: 'read'
        });
      }
    }
    
    return messages;
  };

  const parseWhatsAppExport = (line) => {
    // [21.08.25, 10:15:23] Name: Message
    const match = line.match(/\[(\d{2}\.\d{2}\.\d{2}),\s(\d{2}:\d{2}:\d{2})\]\s([^:]+):\s(.+)/);
    
    if (match) {
      const [, date, time, sender, message] = match;
      const [day, month, year] = date.split('.');
      const timestamp = `20${year}-${month}-${day} ${time}`;
      
      const adminNames = ['Admin', 'Sie', 'You', 'Oktoberfest Team', 'Personal'];
      const messageType = adminNames.some(name => 
        sender.toLowerCase().includes(name.toLowerCase())
      ) ? 'outgoing' : 'incoming';
      
      return {
        message,
        message_type: messageType,
        timestamp,
        status: messageType === 'outgoing' ? 'sent' : 'read'
      };
    }
    
    return null;
  };

  const parseWhatsAppWeb = (line) => {
    // 10:15 Name Message (WhatsApp Web Copy/Paste)
    const match = line.match(/^(\d{1,2}:\d{2})\s+([^0-9\s][^:]*?)\s+(.+)$/);
    
    if (match) {
      const [, time, sender, message] = match;
      const today = new Date();
      const timestamp = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')} ${time}:00`;
      
      const adminNames = ['Admin', 'Sie', 'You', 'Oktoberfest Team', 'Personal'];
      const messageType = adminNames.some(name => 
        sender.toLowerCase().includes(name.toLowerCase())
      ) ? 'outgoing' : 'incoming';
      
      return {
        message,
        message_type: messageType,
        timestamp,
        status: messageType === 'outgoing' ? 'sent' : 'read'
      };
    }
    
    return null;
  };

  const parseSimpleFormat = (line) => {
    // Name: Message
    const match = line.match(/^([^:]+):\s*(.+)$/);
    
    if (match) {
      const [, sender, message] = match;
      const timestamp = new Date().toISOString().slice(0, 19).replace('T', ' ');
      
      const adminNames = ['Admin', 'Sie', 'You', 'Oktoberfest Team', 'Personal'];
      const messageType = adminNames.some(name => 
        sender.toLowerCase().includes(name.toLowerCase())
      ) ? 'outgoing' : 'incoming';
      
      return {
        message,
        message_type: messageType,
        timestamp,
        status: messageType === 'outgoing' ? 'sent' : 'read'
      };
    }
    
    return null;
  };

  return (
    <div className="quick-whatsapp-importer border rounded p-3 mb-3" style={{ backgroundColor: '#e8f5e8' }}>
      <h6 className="mb-3">
        🖥️ Schnell-Import für {userName}
      </h6>
      
      <div className="mb-3">
        <small className="text-muted">
          <strong>💡 PC-Methoden:</strong><br />
          <strong>1. WhatsApp Desktop:</strong> Chat öffnen → Text markieren → Strg+C → hier einfügen<br />
          <strong>2. WhatsApp Web:</strong> web.whatsapp.com → Chat kopieren → hier einfügen<br />
          <strong>3. Backup-Dateien:</strong> Aus Android/iOS Backup extrahierte Chats<br />
          <strong>4. Beliebiges Format:</strong> Automatische Erkennung verschiedener Formate
        </small>
      </div>

      <div className="mb-3">
        <label className="form-label">Chat-Text einfügen:</label>
        <textarea
          className="form-control"
          rows={8}
          value={bulkText}
          onChange={(e) => setBulkText(e.target.value)}
          placeholder="Hier Chat-Text einfügen...

Beispiele:
[21.08.25, 10:15:23] Oktoberfest Team: Hallo!
[21.08.25, 10:16:15] Max Müller: Hallo zurück!

oder:

10:15 Oktoberfest Team Hallo, wie geht es?
10:16 Max Müller Sehr gut, danke!

oder einfach:

Admin: Können Sie morgen arbeiten?
Max: Ja, gerne!"
          disabled={isProcessing}
        />
      </div>

      <div className="mb-3">
        <button
          className="btn btn-success"
          onClick={handleBulkImport}
          disabled={isProcessing || !bulkText.trim()}
        >
          {isProcessing ? (
            <>
              <span className="spinner-border spinner-border-sm me-2"></span>
              Verarbeite...
            </>
          ) : (
            '📥 Text importieren'
          )}
        </button>
      </div>

      {importStatus && (
        <div className={`alert ${importStatus.includes('✅') ? 'alert-success' : 
                               importStatus.includes('❌') ? 'alert-danger' : 'alert-info'}`}>
          {importStatus}
        </div>
      )}
    </div>
  );
}

export default QuickWhatsAppImporter;
